/* ------------------------------------------------------------------
 * CollectionsPage — dedicated V2 route that renders the existing
 * Curated Collections experience without taking the user back to the
 * legacy LandingPage.
 *
 * Behaviour parity with /v1#curated-collections:
 *   - same 5 scroll-cards (Habits+Compounding, Purpose+Wisdom, etc.)
 *   - same Infographic viewer (multi-language)
 *   - same Podcast / Audio player (multi-language)
 *   - same data (pairingResources verbatim)
 *   - same CSS classes from LandingPage.css
 *
 * Visual reskin into the frozen v2 parchment system will follow.
 * For now the existing experience is presented inside a parchment
 * shell so the user stays in the V2 application.
 * ---------------------------------------------------------------- */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  X, ArrowLeft, Leaf, Compass, Coins, Target, Activity,
} from 'lucide-react';
import '../styles/LandingPage.css';

const iconMap = { Leaf, Compass, Coins, Target, Activity };

const springConfig = { type: 'spring', damping: 22, stiffness: 220 };

/* Verbatim copy of pairingResources from LandingPage.jsx */
const pairingResources = {
  1: {
    title: 'Habits + Compounding',
    covers: [
      'https://covers.openlibrary.org/b/isbn/9780735211292-L.jpg',
      'https://books.google.com/books/content?id=P28DsLkv5cgC&printsec=frontcover&img=1&zoom=1&source=gbs_api',
    ],
    bookNames: ['Atomic Habits', 'The Compound Effect'],
    audio: {
      en: '/audio/pairing1/audio_en.m4a',
      hi: '/audio/pairing1/audio_hi.m4a',
      gu: '/audio/pairing1/audio_gu.m4a',
      mr: '/audio/pairing1/audio_mr.m4a',
      kn: '/audio/pairing1/audio_kn.m4a',
    },
    infographic: {
      en: '/collections/pairing1/infographic_en.webp',
      hi: '/collections/pairing1/infographic_hi.webp',
      gu: '/collections/pairing1/infographic_gu.webp',
      mr: '/collections/pairing1/infographic_mr.webp',
      kn: '/collections/pairing1/infographic_kn.webp',
    },
  },
  2: {
    title: 'Purpose + Wisdom',
    bookNames: ['Ikigai', "Life's Amazing Secrets"],
    infographic: {
      en: '/collections/pairing2/infographic_en.webp',
      hi: '/collections/pairing2/infographic_hi.webp',
      gu: '/collections/pairing2/infographic_gu.webp',
      mr: '/collections/pairing2/infographic_mr.webp',
      kn: '/collections/pairing2/infographic_kn.webp',
    },
  },
  3: {
    title: 'Mind + Money',
    bookNames: ['Think and Grow Rich', 'Rich Dad Poor Dad'],
    infographic: {
      en: '/collections/pairing3/infographic_en.webp',
      hi: '/collections/pairing3/infographic_hi.webp',
      gu: '/collections/pairing3/infographic_gu.webp',
      mr: '/collections/pairing3/infographic_mr.webp',
      kn: '/collections/pairing3/infographic_kn.webp',
    },
  },
  4: {
    title: 'Focus + Execution',
    bookNames: ['The ONE Thing', 'Deep Work'],
    infographic: {
      en: '/collections/pairing4/infographic_en.webp',
      hi: '/collections/pairing4/infographic_hi.webp',
      gu: '/collections/pairing4/infographic_gu.webp',
      mr: '/collections/pairing4/infographic_mr.webp',
      kn: '/collections/pairing4/infographic_kn.webp',
    },
  },
  5: {
    title: 'Mindset + Action',
    bookNames: ['Attitude is Everything', "Build, Don't Talk"],
    infographic: {
      en: '/collections/pairing5/infographic_en.webp',
      hi: '/collections/pairing5/infographic_hi.webp',
      gu: '/collections/pairing5/infographic_gu.webp',
      mr: '/collections/pairing5/infographic_mr.webp',
      kn: '/collections/pairing5/infographic_kn.webp',
    },
  },
};

const pickerLanguages = [
  { code: 'en', glyph: 'EN', label: 'English' },
  { code: 'hi', glyph: 'हि', label: 'हिंदी' },
  { code: 'gu', glyph: 'ગુ', label: 'ગુજરાતી' },
  { code: 'mr', glyph: 'म', label: 'मराठी' },
  { code: 'kn', glyph: 'ಕ', label: 'ಕನ್ನಡ' },
];

export default function CollectionsPage() {
  const navigate = useNavigate();
  const [resourcePicker, setResourcePicker] = useState(null);
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [infographicViewer, setInfographicViewer] = useState(null);

  const getResourceUrl = (langCode) => {
    if (!resourcePicker) return '#';
    const { pairingIndex, type } = resourcePicker;
    return pairingResources[pairingIndex]?.[type]?.[langCode] || '#';
  };

  const handleAudioLangPick = (e, langCode) => {
    if (!resourcePicker || resourcePicker.type !== 'audio') return;
    e.preventDefault();
    const { pairingIndex } = resourcePicker;
    const lang = pickerLanguages.find((l) => l.code === langCode);
    setAudioPlayer({ pairingIndex, langCode, langLabel: lang?.label || langCode });
    setResourcePicker(null);
  };

  const handleInfographicLangPick = (e, langCode) => {
    if (!resourcePicker || resourcePicker.type !== 'infographic') return;
    e.preventDefault();
    const { pairingIndex } = resourcePicker;
    const lang = pickerLanguages.find((l) => l.code === langCode);
    setInfographicViewer({ pairingIndex, langCode, langLabel: lang?.label || langCode });
    setResourcePicker(null);
  };

  return (
    <div
      className="landing-page landing-page-master"
      data-testid="collections-page"
      style={{ minHeight: '100vh' }}
    >
      {/* Back-to-home brass strip — matches the v2 parchment shell tone */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          padding: '12px 20px',
          background:
            'linear-gradient(180deg, rgba(26,14,7,.92) 0%, rgba(26,14,7,.62) 100%)',
          backdropFilter: 'blur(6px)',
          borderBottom: '1px solid rgba(163,126,42,.35)',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <button
          onClick={() => navigate('/')}
          data-testid="back-to-home"
          aria-label="Back to Home"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            color: '#e9d9b3',
            background: 'transparent',
            border: '1px solid rgba(163,126,42,.5)',
            borderRadius: 4,
            fontFamily: "'Cinzel', serif",
            fontSize: '.74rem',
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={14} /> Home
        </button>
        <span
          style={{
            color: '#c19a4b',
            fontFamily: "'Cinzel', serif",
            fontSize: '.74rem',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
          }}
        >
          Curated Collections
        </span>
      </div>

      {/* ─────── Curated Collections Section ─────── */}
      <section id="curated-collections" className="curated-collections-section">
        <div className="curated-collections-container">
          <motion.div
            className="curated-collections-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <h2 className="library-title">
              Curated <span className="accent-last-word">Collections</span>
            </h2>
            <p className="library-subtitle">
              Two books. One powerful idea. Ready in your language.
            </p>
          </motion.div>

          <div className="curated-collections-grid scroll-grid">
            {[
              { id: 1, slug: 'habits_compounding', icon: 'Leaf',     ribbonColor: '#4F6B3D' },
              { id: 2, slug: 'purpose_wisdom',     icon: 'Compass',  ribbonColor: '#2A3F66' },
              { id: 3, slug: 'mind_money',         icon: 'Coins',    ribbonColor: '#7B5530' },
              { id: 4, slug: 'focus_execution',    icon: 'Target',   ribbonColor: '#3F7A78' },
              { id: 5, slug: 'mindset_action',     icon: 'Activity', ribbonColor: '#6B2C2A' },
            ].map((p, idx) => {
              const data = pairingResources[p.id];
              const Icon = iconMap[p.icon];
              return (
                <motion.div
                  key={p.id}
                  className="scroll-card"
                  data-testid={`scroll-card-${p.id}`}
                  style={{
                    backgroundImage: `url(/scrolls/${p.slug}.png)`,
                    '--ribbon-color': p.ribbonColor,
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...springConfig, delay: 0.04 * idx }}
                >
                  <span className="scroll-ribbon" aria-hidden="true">
                    {Icon && <Icon strokeWidth={2.4} />}
                  </span>

                  <div className="scroll-text">
                    <h3 className="scroll-title">{data.title}</h3>
                    <span className="scroll-divider" aria-hidden="true" />
                    <p className="scroll-book">{data.bookNames[0]}</p>
                    <span className="scroll-plus" aria-hidden="true">+</span>
                    <p className="scroll-book">{data.bookNames[1]}</p>
                    <span className="scroll-divider" aria-hidden="true" />

                    <div className="scroll-actions">
                      <button
                        type="button"
                        className="scroll-stamp scroll-stamp-active"
                        data-testid={`scroll-infographic-${p.id}`}
                        onClick={() => setResourcePicker({ pairingIndex: p.id, type: 'infographic' })}
                      >
                        Infographic
                      </button>
                      {data.audio ? (
                        <button
                          type="button"
                          className="scroll-stamp scroll-stamp-active"
                          data-testid={`scroll-podcast-${p.id}`}
                          onClick={() => setResourcePicker({ pairingIndex: p.id, type: 'audio' })}
                        >
                          Podcast
                        </button>
                      ) : (
                        <span className="scroll-stamp scroll-stamp-soon" aria-disabled="true">
                          Podcast · Soon
                        </span>
                      )}
                    </div>

                    <div className="scroll-langs">
                      <span>EN</span><i>|</i>
                      <span>हि</span><i>|</i>
                      <span>ગુ</span><i>|</i>
                      <span>म</span><i>|</i>
                      <span>ಕ</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="scroll-tagline" aria-hidden="false">
            <span className="scroll-tagline-quote scroll-tagline-quote-left">❝</span>
            <span className="scroll-tagline-text">
              Curated wisdom. Timeless lessons. Meaningful growth.
            </span>
            <span className="scroll-tagline-quote scroll-tagline-quote-right">❞</span>
          </div>
          <span className="scroll-tagline-divider" aria-hidden="true">
            <span className="scroll-tagline-line" />
            <span className="scroll-tagline-mark">❦</span>
            <span className="scroll-tagline-line" />
          </span>
        </div>
      </section>

      {/* ─────── Resource Language Picker Modal ─────── */}
      <AnimatePresence>
        {resourcePicker && (
          <motion.div
            className="resource-picker-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setResourcePicker(null)}
            data-testid="resource-picker-overlay"
          >
            <motion.div
              className="resource-picker-modal"
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="resource-picker-close"
                onClick={() => setResourcePicker(null)}
                aria-label="Close"
                data-testid="resource-picker-close"
              >
                <X size={18} />
              </button>
              <p className="resource-picker-eyebrow">
                {resourcePicker.type === 'audio' ? 'Podcast' : 'Infographic'}
              </p>
              <h3 className="resource-picker-title">Pick your language</h3>
              <p className="resource-picker-subtitle">Opens in a new tab.</p>
              <div className="resource-picker-grid">
                {pickerLanguages.map((lang) => (
                  <a
                    key={lang.code}
                    href={getResourceUrl(lang.code)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-picker-lang"
                    onClick={(e) => {
                      if (resourcePicker?.type === 'audio') {
                        handleAudioLangPick(e, lang.code);
                      } else if (resourcePicker?.type === 'infographic') {
                        handleInfographicLangPick(e, lang.code);
                      } else {
                        setResourcePicker(null);
                      }
                    }}
                    data-testid={`resource-picker-lang-${lang.code}`}
                  >
                    <span className="resource-picker-glyph">{lang.glyph}</span>
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─────── In-app Audio Player Modal ─────── */}
      <AnimatePresence>
        {audioPlayer && (() => {
          const pair = pairingResources[audioPlayer.pairingIndex];
          const src = pair?.audio?.[audioPlayer.langCode];
          return (
            <motion.div
              className="audio-player-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setAudioPlayer(null)}
              data-testid="audio-player-overlay"
            >
              <motion.div
                className="audio-player-modal"
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 15, scale: 0.98 }}
                transition={{ duration: 0.28, ease: 'easeOut' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="audio-player-bg" aria-hidden="true">
                  <img src={pair.covers[0]} alt="" className="audio-player-bg-img audio-player-bg-img--left" />
                  <img src={pair.covers[1]} alt="" className="audio-player-bg-img audio-player-bg-img--right" />
                  <div className="audio-player-bg-overlay" />
                </div>

                <button
                  className="audio-player-close"
                  onClick={() => setAudioPlayer(null)}
                  aria-label="Close player"
                  data-testid="audio-player-close"
                >
                  <X size={20} />
                </button>

                <div className="audio-player-content">
                  <p className="audio-player-eyebrow">Now Playing — {audioPlayer.langLabel}</p>
                  <h3 className="audio-player-title">{pair.title}</h3>

                  <div className="audio-player-covers">
                    <div className="audio-player-cover-wrap">
                      <img src={pair.covers[0]} alt={pair.bookNames[0]} className="audio-player-cover" />
                      <p className="audio-player-cover-label">{pair.bookNames[0]}</p>
                    </div>
                    <div className="audio-player-plus">+</div>
                    <div className="audio-player-cover-wrap">
                      <img src={pair.covers[1]} alt={pair.bookNames[1]} className="audio-player-cover" />
                      <p className="audio-player-cover-label">{pair.bookNames[1]}</p>
                    </div>
                  </div>

                  <audio
                    key={src}
                    src={src}
                    controls
                    autoPlay
                    preload="auto"
                    className="audio-player-audio"
                    data-testid="audio-player-audio"
                  >
                    Your browser does not support the audio element.
                  </audio>

                  <p className="audio-player-footnote">Less Noise · More Thought</p>
                  <p className="audio-player-credit">
                    Original educational interpretation by iCraftz™. Book titles, covers, and quoted passages are property of their respective authors and publishers. Not affiliated with or endorsed by any publisher.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* ─────── In-app Infographic Viewer Modal ─────── */}
      <AnimatePresence>
        {infographicViewer && (() => {
          const pair = pairingResources[infographicViewer.pairingIndex];
          const src = pair?.infographic?.[infographicViewer.langCode];
          return (
            <motion.div
              className="infographic-viewer-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setInfographicViewer(null)}
              data-testid="infographic-viewer-overlay"
            >
              <motion.div
                className="infographic-viewer-modal"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="infographic-viewer-close"
                  onClick={() => setInfographicViewer(null)}
                  aria-label="Close infographic"
                  data-testid="infographic-viewer-close"
                >
                  <X size={20} />
                </button>
                <div className="infographic-viewer-header">
                  <p className="infographic-viewer-eyebrow">
                    Infographic — {infographicViewer.langLabel}
                  </p>
                  <h3 className="infographic-viewer-title">{pair.title}</h3>
                </div>
                <div className="infographic-viewer-image-wrap">
                  <img
                    src={src}
                    alt={`${pair.title} — ${infographicViewer.langLabel} infographic`}
                    className="infographic-viewer-image"
                    data-testid="infographic-viewer-image"
                  />
                </div>
                <p className="infographic-viewer-hint">
                  Pinch or scroll to zoom. Tap outside to close.
                </p>
                <p className="infographic-viewer-credit">
                  Original educational interpretation by iCraftz™. Any direct quotes are attributed to their authors. Book titles and covers are property of their respective publishers.
                </p>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}
